import { Link, useOutletContext } from "react-router-dom";
import { CalendarClock, Zap } from "lucide-react";

const PLANS = [
  { key: "starter", name: "Starter Scale", credits: 150 },
  { key: "growth", name: "Growth Pro", credits: 250 },
  { key: "empire", name: "Empire Velocity", credits: 400 },
];

export default function CreditsPage() {
  const { customer } = useOutletContext();
  const used = Math.max(0, customer.credits_total - customer.credits_remaining);
  const usedPct =
    customer.credits_total > 0
      ? Math.round((used / customer.credits_total) * 100)
      : 0;
  const renewalDate = new Date(customer.billing_reset_date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const daysLeft = Math.max(0, Math.ceil((new Date(customer.billing_reset_date) - new Date()) / 86400000));

  return (
    <div data-testid="credits-page">
      <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-sky-400">Grapho Studio</p>
      <h1 className="mt-2 text-2xl font-extrabold tracking-tight text-white sm:text-3xl">Credits and Plan</h1>

      <div className="glass mt-8 rounded-2xl p-6" data-testid="credits-summary-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Design Credits</p>
          <span
            data-testid="subscription-status-chip"
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${
              customer.plan_status === "cancelled"
                ? "border-rose-400/40 bg-rose-400/10 text-rose-300"
                : customer.cancellation_requested_at
                  ? "border-amber-400/40 bg-amber-400/10 text-amber-300"
                  : "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
            }`}
          >
            {customer.plan_status === "cancelled"
              ? "Cancelled"
              : customer.cancellation_requested_at
                ? "Cancellation requested"
                : "Active"}
          </span>
        </div>
        <p data-testid="credits-remaining-big" className="mt-2 text-4xl font-extrabold tracking-tight text-white">
          {customer.credits_remaining}
          <span className="text-lg font-bold text-slate-500"> / {customer.credits_total} remaining</span>
        </p>
        <div className="mt-4 h-2 overflow-hidden rounded-full bg-white/10">
          <div className="h-full rounded-full bg-gradient-to-r from-sky-400 to-amber-400" style={{ width: `${100 - usedPct}%` }} />
        </div>
        <div className="mt-3 grid gap-1 text-xs text-slate-400 sm:grid-cols-3">
          <p data-testid="credits-used">Credits used: <span className="font-semibold text-slate-200">{used}</span></p>
          <p data-testid="credits-plan-name">Plan: <span className="font-semibold text-amber-300">{customer.plan_name}</span></p>
          <p data-testid="credits-renewal">Renews: <span className="font-semibold text-slate-200">{renewalDate}</span></p>
        </div>
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1 text-xs font-semibold text-sky-300">
          <CalendarClock className="h-3 w-3" />
          {daysLeft} day{daysLeft === 1 ? "" : "s"} left in cycle
        </p>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">
          1 credit = 1 production request. Credits reset at the start of each cycle and don't roll over.
          References are free — they never cost credits.
        </p>
      </div>

      <div className="glass mt-4 rounded-2xl p-6" data-testid="upgrade-options">
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-amber-300">Upgrade options</p>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          {PLANS.map((p) => (
            <a
              key={p.key}
              href="/#pricing"
              data-testid={`upgrade-${p.key}-link`}
              className="rounded-xl border border-white/10 bg-white/5 p-4 transition-colors hover:border-amber-400/40"
            >
              <p className="text-sm font-bold text-white">{p.name}</p>
              <p className="mt-1 text-xs text-slate-400">{p.credits} credits / cycle</p>
            </a>
          ))}
        </div>
        <p className="mt-4 flex items-center gap-1.5 text-xs text-slate-500">
          <Zap className="h-3 w-3 text-amber-300" />
          Need a custom volume? Lock in a per-design rate on the pricing page.
        </p>
        <a
          href="/#pricing"
          data-testid="purchase-credits-link"
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-xs font-bold text-[#1A1204] transition-colors hover:bg-amber-300"
        >
          Purchase Additional Credits
        </a>
      </div>
    </div>
  );
}
